"use client"

import type { CSSProperties } from "react"
import { useEffect, useMemo, useState } from "react"
import { useQueries, useQuery } from "@tanstack/react-query"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"
import { getPortfolioAccounts, getPortfolioBalance } from "@/lib/api/portfolio"
import { getCurrentPrices } from "@/lib/api/prices"
import { getAutoTradingStocks } from "@/lib/api/autotrading"
import { AddToAutoTradingButton } from "@/components/watchlist/AddToAutoTradingButton"
import { ThemeBadges } from "@/components/shared/ThemeBadges"
import { StockDetailPanel, type DetailTabKey } from "@/components/stock-detail/StockDetailPanel"
import { TablePagination } from "@/components/shared/TablePagination"
import { useResizableSplit } from "@/hooks/use-resizable-split"
import { useFillViewportMain } from "@/hooks/use-fill-viewport-main"
import type { PortfolioHolding } from "@/types/portfolio"
import type { CurrentPrice } from "@/types/price"

const HOLDINGS_PAGE_SIZE = 50

// 한국 주식 색상: 양수=빨강, 음수=파랑
const C_POS = "#e05c5c"
const C_NEG = "#4d9cf0"
const C_MUT = "#71717a"

const PANEL: CSSProperties = {
  background: "#1a1a2e",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 14,
}

const CARD: CSSProperties = {
  background: "#1e2235",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: "16px 18px",
}

function plStyle(n: number): CSSProperties {
  if (n > 0) return { color: C_POS }
  if (n < 0) return { color: C_NEG }
  return { color: C_MUT }
}

function fmtNum(n: number) {
  return n.toLocaleString()
}

function fmtRate(n: number) {
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`
}

// ── Summary Panel ─────────────────────────────────────────────────────────────

interface SummaryItemProps {
  label: string
  amount?: number
  rate?: number
  colored?: boolean
  isLoading: boolean
}

function SummaryItem({ label, amount, rate, colored, isLoading }: SummaryItemProps) {
  const numVal = rate !== undefined ? rate : amount
  const style: CSSProperties =
    colored && numVal !== undefined ? plStyle(numVal) : { color: "#e4e4e7" }

  return (
    <div style={CARD} className="flex flex-col gap-1">
      <span style={{ fontSize: 11, color: C_MUT }}>{label}</span>
      {isLoading ? (
        <Skeleton className="h-5 w-24 bg-white/5" />
      ) : rate !== undefined ? (
        <span style={{ ...style, fontSize: 14, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
          {fmtRate(rate)}
        </span>
      ) : (
        <span className="flex items-baseline gap-1">
          <span style={{ ...style, fontSize: 14, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
            {amount !== undefined ? fmtNum(amount) : "-"}
          </span>
          {amount !== undefined && (
            <span style={{ fontSize: 11, color: C_MUT }}>원</span>
          )}
        </span>
      )}
    </div>
  )
}

// ── PL Banner ─────────────────────────────────────────────────────────────────

function PlBanner({ pl, rate }: { pl: number; rate: number }) {
  const isPos  = pl >= 0
  const color  = isPos ? C_POS : C_NEG
  const bg     = isPos ? "rgba(224,92,92,0.06)"  : "rgba(77,156,240,0.06)"
  const border = isPos ? "rgba(224,92,92,0.15)"  : "rgba(77,156,240,0.15)"
  const arrow  = isPos ? "↗" : "↘"

  return (
    <div
      style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 8 }}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-mono"
    >
      <span>{arrow}</span>
      <span>
        평가손익 {pl >= 0 ? "+" : ""}{fmtNum(pl)}원 · 수익률 {fmtRate(rate)}
      </span>
    </div>
  )
}

// ── Return Rate Badge ─────────────────────────────────────────────────────────

function ReturnBadge({ rate }: { rate: number }) {
  const isPos = rate >= 0
  return (
    <span
      style={{
        background: isPos ? "rgba(224,92,92,0.12)" : "rgba(77,156,240,0.12)",
        color: isPos ? C_POS : C_NEG,
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: 12,
        fontVariantNumeric: "tabular-nums",
      }}
      className="font-mono"
    >
      {fmtRate(rate)}
    </span>
  )
}

// ── Holding Ratio with Progress Bar ──────────────────────────────────────────

function HoldingRatioCell({ ratio }: { ratio: number }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <div style={{ width: 52, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(ratio, 100)}%`, height: "100%", background: C_NEG, borderRadius: 3 }} />
      </div>
      <span className="font-mono text-xs" style={{ color: C_MUT, minWidth: 36, textAlign: "right" }}>
        {ratio.toFixed(1)}%
      </span>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

const ROW_BORDER: CSSProperties = { borderColor: "rgba(255,255,255,0.04)" }

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i} style={ROW_BORDER}>
          {Array.from({ length: 11 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full bg-white/5" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

function MobileSkeletonCards() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-2/3 bg-white/5" />
          <Skeleton className="h-3 w-1/3 bg-white/5" />
        </div>
      ))}
    </>
  )
}

// ── Holdings Table Row ────────────────────────────────────────────────────────

function HoldingRow({ h, live, selected, onSelect, inAutoTrading }: { h: PortfolioHolding; live?: CurrentPrice; selected: boolean; onSelect: (h: PortfolioHolding) => void; inAutoTrading: boolean }) {
  const price      = live?.currentPrice ?? h.currentPrice
  const profitLoss = live != null
    ? Math.round((live.currentPrice - Number(h.avgBuyPrice)) * h.quantity)
    : h.profitLoss
  const returnRate = live != null && Number(h.avgBuyPrice) > 0
    ? ((live.currentPrice - Number(h.avgBuyPrice)) / Number(h.avgBuyPrice)) * 100
    : h.returnRate

  return (
    <TableRow
      style={selected ? { ...ROW_BORDER, background: "rgba(255,255,255,0.06)" } : ROW_BORDER}
      className="cursor-pointer hover:bg-white/[0.04]"
      onClick={() => onSelect(h)}
    >
      <TableCell>
        <div className="font-medium text-zinc-100">{h.stockName}</div>
        <div style={{ fontSize: 11, color: C_MUT }}>{h.stockCode}</div>
      </TableCell>
      <TableCell className="text-xs" style={{ color: C_MUT }}>{h.sector ?? "-"}</TableCell>
      <TableCell className="text-xs" style={{ color: C_MUT }}>{h.industry ?? "-"}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <ThemeBadges themes={h.themes} />
      </TableCell>
      <TableCell className="text-right font-mono text-zinc-200">{fmtNum(price)}</TableCell>
      <TableCell className="text-right font-mono text-zinc-200">{fmtNum(Math.round(Number(h.avgBuyPrice)))}</TableCell>
      <TableCell className="text-right font-mono text-zinc-200">{fmtNum(h.quantity)}</TableCell>
      <TableCell className="text-right font-mono" style={plStyle(profitLoss)}>
        {profitLoss >= 0 ? "+" : ""}{fmtNum(profitLoss)}
      </TableCell>
      <TableCell className="text-right">
        <ReturnBadge rate={returnRate} />
      </TableCell>
      <TableCell>
        <HoldingRatioCell ratio={h.holdingRatio} />
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <AddToAutoTradingButton
          stockCode={h.stockCode}
          added={inAutoTrading}
          strategyType="Manual"
          tooltipLabel="자동매매 종목에 추가"
          addedTooltipLabel="이미 자동매매 종목으로 추가됨"
          successMessage="자동매매 종목에 추가되었습니다"
        />
      </TableCell>
    </TableRow>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PortfolioPage() {
  const [selectedAccount, setSelectedAccount] = useState("")

  const { data: accounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ["portfolio", "accounts"],
    queryFn: getPortfolioAccounts,
    staleTime: 0,
    refetchOnMount: true,
    retry: 1,
  })

  const effectiveAccount = selectedAccount || accounts[0]?.accountNumber || ""

  // 화면엔 선택된 계좌만 표시하지만, 잔고는 전 계좌를 각각 1회씩 불러와서
  // 현재가 폴링 대상(stockCodes)이 전 계좌 보유종목을 커버하게 한다. 보유종목 리스트는
  // 매매 체결 시에만 바뀌므로 반복 폴링 없이도 충분하다.
  const balanceQueries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: ["portfolio", "balance", a.accountNumber],
      queryFn: () => getPortfolioBalance(a.accountNumber),
      retry: 1,
    })),
  })

  const effectiveIndex = accounts.findIndex((a) => a.accountNumber === effectiveAccount)
  const balance = balanceQueries[effectiveIndex]?.data
  const balanceLoading = effectiveIndex === -1 ? accountsLoading : balanceQueries[effectiveIndex].isLoading

  const stockCodes = useMemo(
    () => Array.from(new Set(balanceQueries.flatMap((q) => q.data?.holdings.map((h) => h.stockCode) ?? []))),
    [balanceQueries]
  )

  const { data: currentPricesList = [] } = useQuery({
    queryKey: ["prices", "current", "portfolio", stockCodes],
    queryFn: () => getCurrentPrices(stockCodes),
    enabled: stockCodes.length > 0,
    refetchInterval: 30_000,
    staleTime: 25_000,
  })

  const currentPricesMap = Object.fromEntries(currentPricesList.map((p) => [p.stockCode, p]))

  const { data: autoTradingStocks = [] } = useQuery({
    queryKey: ["autotrading", "stocks"],
    queryFn: getAutoTradingStocks,
  })
  const autoTradingCodes = useMemo(() => new Set(autoTradingStocks.map((s) => s.stockCode)), [autoTradingStocks])

  // 현재가 기준 합계 재계산
  const liveTotals = balance && currentPricesList.length > 0
    ? (() => {
        const evalAmount  = balance.holdings.reduce((acc, h) => {
          const price = currentPricesMap[h.stockCode]?.currentPrice ?? h.currentPrice
          return acc + price * h.quantity
        }, 0)
        const profitLoss  = evalAmount - Number(balance.totalBuyAmount)
        const returnRate  = balance.totalBuyAmount > 0
          ? (profitLoss / Number(balance.totalBuyAmount)) * 100
          : 0
        return { evalAmount, profitLoss, returnRate }
      })()
    : null

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-zinc-100">포트폴리오</h1>
        <Select
          value={effectiveAccount}
          onValueChange={setSelectedAccount}
          disabled={accountsLoading || accounts.length === 0}
        >
          <SelectTrigger className="w-56 border-zinc-700 bg-zinc-900 text-white">
            <SelectValue placeholder={accountsLoading ? "로딩 중..." : "계좌 선택"} />
          </SelectTrigger>
          <SelectContent className="border-zinc-700 bg-zinc-800">
            {accounts.map((a) => (
              <SelectItem
                key={a.id}
                value={a.accountNumber}
                className="!text-white hover:!text-white focus:!text-white data-[highlighted]:!text-white data-[highlighted]:bg-gray-700"
              >
                {a.accountName ? `${a.accountName} (${a.accountNumber})` : a.accountNumber}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards — desktop: 5열, mobile: 2열 */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <SummaryItem label="추정예탁자산" amount={balance?.depositAsset}                              isLoading={balanceLoading} />
        <SummaryItem label="총매입금액"   amount={balance?.totalBuyAmount}                           isLoading={balanceLoading} />
        <SummaryItem label="총평가금액"   amount={liveTotals?.evalAmount  ?? balance?.totalEvalAmount} isLoading={balanceLoading} />
        <SummaryItem label="총평가손익"   amount={liveTotals?.profitLoss  ?? balance?.totalProfitLoss} colored isLoading={balanceLoading} />
        <SummaryItem label="총수익률"     rate={liveTotals?.returnRate    ?? balance?.totalReturnRate}  colored isLoading={balanceLoading} />
      </div>

      {/* PL Banner */}
      {balance && !balanceLoading && (
        <PlBanner
          pl={liveTotals?.profitLoss ?? balance.totalProfitLoss}
          rate={liveTotals?.returnRate ?? balance.totalReturnRate}
        />
      )}

      {/* Holdings: md+ 테이블 */}
      <div className="hidden overflow-hidden md:block" style={{ ...PANEL, borderRadius: 12 }}>
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: "rgba(255,255,255,0.06)" }} className="hover:bg-transparent">
              {["종목명", "섹터", "업종", "테마", "현재가", "매입가", "보유수량", "평가손익", "수익률", "보유비중"].map((h, i) => (
                <TableHead
                  key={h}
                  style={{ fontSize: 11, color: C_MUT, fontWeight: 400 }}
                  className={i > 3 ? "text-right" : ""}
                >
                  {h}
                </TableHead>
              ))}
              <TableHead style={{ fontSize: 11, color: C_MUT, fontWeight: 400 }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {balanceLoading ? (
              <SkeletonRows />
            ) : !balance || balance.holdings.length === 0 ? (
              <TableRow style={ROW_BORDER}>
                <TableCell colSpan={11} className="py-12 text-center" style={{ color: C_MUT }}>
                  보유 종목이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              balance.holdings.map((h) => (
                <HoldingRow
                  key={h.stockCode}
                  h={h}
                  live={currentPricesMap[h.stockCode]}
                  onNavigate={openStockChart}
                  inAutoTrading={autoTradingCodes.has(h.stockCode)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Holdings: 모바일 카드 */}
      <div className="overflow-hidden md:hidden" style={{ ...PANEL, borderRadius: 12 }}>
        {balanceLoading ? (
          <MobileSkeletonCards />
        ) : !balance || balance.holdings.length === 0 ? (
          <div className="py-12 text-center" style={{ color: C_MUT }}>보유 종목이 없습니다.</div>
        ) : (
          balance.holdings.map((h) => {
            const live       = currentPricesMap[h.stockCode]
            const price      = live?.currentPrice ?? h.currentPrice
            const profitLoss = live != null
              ? Math.round((live.currentPrice - Number(h.avgBuyPrice)) * h.quantity)
              : h.profitLoss
            const returnRate = live != null && Number(h.avgBuyPrice) > 0
              ? ((live.currentPrice - Number(h.avgBuyPrice)) / Number(h.avgBuyPrice)) * 100
              : h.returnRate
            return (
              <div
                key={h.stockCode}
                className="flex cursor-pointer flex-col gap-1.5 px-4 py-3 active:bg-white/5"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                onClick={() => openStockChart(h.stockCode)}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-medium text-zinc-100">{h.stockName}</span>
                  <span className="font-mono text-sm text-zinc-200">{fmtNum(price)}</span>
                </div>
                {(h.sector || h.industry) && (
                  <span className="truncate text-xs" style={{ color: C_MUT }}>
                    {[h.sector, h.industry].filter(Boolean).join(" · ")}
                  </span>
                )}
                {h.themes.length > 0 && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ThemeBadges themes={h.themes} />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 text-xs" style={{ color: C_MUT }}>
                  <span className="font-mono">매입 {fmtNum(Math.round(Number(h.avgBuyPrice)))}</span>
                  <span className="font-mono">{fmtNum(h.quantity)}주</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm" style={plStyle(profitLoss)}>
                    {profitLoss >= 0 ? "+" : ""}{fmtNum(profitLoss)}
                  </span>
                  <div className="flex items-center gap-1">
                    <ReturnBadge rate={returnRate} />
                    <span onClick={(e) => e.stopPropagation()}>
                      <AddToAutoTradingButton
                        stockCode={h.stockCode}
                        added={autoTradingCodes.has(h.stockCode)}
                        strategyType="Manual"
                        tooltipLabel="자동매매 종목에 추가"
                        addedTooltipLabel="이미 자동매매 종목으로 추가됨"
                        successMessage="자동매매 종목에 추가되었습니다"
                      />
                    </span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
